import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Modal,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { importReportTemplates } from "api/reportTemplate";
import {
  MAX_IMPORT_TEMPLATES,
  parseImportFile,
  downloadCsvTemplate,
} from "utils/reportTemplateImport";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "60%",
  maxHeight: "88vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: "15px",
  boxShadow: 24,
  p: 4,
};

const ACTION_STYLES = {
  created: { label: "Created", color: "#047857", bg: "#ECFDF5" },
  wouldCreate: { label: "Would Create", color: "#047857", bg: "#ECFDF5" },
  updated: { label: "Updated", color: "#1D4ED8", bg: "#EFF6FF" },
  wouldUpdate: { label: "Would Update", color: "#1D4ED8", bg: "#EFF6FF" },
  skipped: { label: "Skipped", color: "#B45309", bg: "#FEF3C7" },
  wouldSkip: { label: "Would Skip", color: "#B45309", bg: "#FEF3C7" },
  failed: { label: "Failed", color: "#DC2626", bg: "#FEF2F2" },
};

const StatCard = ({ label, value, color }) => (
  <Grid item xs={6} sm={3}>
    <Box
      sx={{ textAlign: "center", p: 1.5, bgcolor: "white", borderRadius: 2 }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight="bold" sx={{ color }}>
        {value ?? 0}
      </Typography>
    </Box>
  </Grid>
);

const SummaryBlock = ({ title, summary }) => (
  <Box
    sx={{
      mt: 3,
      p: 2,
      bgcolor: "#F8F9FF",
      borderRadius: 2,
      border: "1px solid #E0E4FF",
    }}
  >
    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Grid container spacing={1}>
      <StatCard label="Create" value={summary.created} color="#047857" />
      <StatCard label="Update" value={summary.updated} color="#1D4ED8" />
      <StatCard label="Skip" value={summary.skipped} color="#B45309" />
      <StatCard label="Failed" value={summary.failed} color="#DC2626" />
    </Grid>

    {Array.isArray(summary.results) && summary.results.length > 0 && (
      <Box sx={{ mt: 2, maxHeight: 260, overflowY: "auto" }}>
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
        >
          <thead>
            <tr style={{ background: "#F0F4FF" }}>
              <th style={{ padding: 6, textAlign: "left", width: 44 }}>#</th>
              <th style={{ padding: 6, textAlign: "left" }}>Template</th>
              <th style={{ padding: 6, textAlign: "left", width: 130 }}>
                Result
              </th>
              <th style={{ padding: 6, textAlign: "left" }}>Detail</th>
            </tr>
          </thead>
          <tbody>
            {summary.results.map((r) => {
              const a = ACTION_STYLES[r.action] || ACTION_STYLES.failed;
              return (
                <tr key={r.index} style={{ borderBottom: "1px solid #F0F0F0" }}>
                  <td style={{ padding: 6 }}>{r.index + 1}</td>
                  <td style={{ padding: 6 }}>{r.name}</td>
                  <td style={{ padding: 6 }}>
                    <span
                      style={{
                        color: a.color,
                        background: a.bg,
                        borderRadius: 6,
                        padding: "2px 8px",
                        fontWeight: 600,
                        fontSize: 12,
                      }}
                    >
                      {a.label}
                    </span>
                  </td>
                  <td style={{ padding: 6 }}>
                    {r.reason && (
                      <div style={{ color: "#DC2626" }}>{r.reason}</div>
                    )}
                    {(r.warnings || []).map((w, i) => (
                      <div key={i} style={{ color: "#B45309" }}>
                        ⚠ {w}
                      </div>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
    )}
  </Box>
);

const ReportTemplateImportModal = ({ open, onClose, onImported }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [parseError, setParseError] = useState("");
  const [mode, setMode] = useState("skip");
  const [preview, setPreview] = useState(null);
  const [finalResult, setFinalResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const resetPreview = () => {
    setPreview(null);
    setFinalResult(null);
  };

  const resetAll = () => {
    setFile(null);
    setTemplates([]);
    setParseError("");
    setMode("skip");
    setPreview(null);
    setFinalResult(null);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const ingestFile = (selected) => {
    if (!selected) return;
    resetPreview();
    setParseError("");
    setTemplates([]);
    const ext = selected.name.split(".").pop().toLowerCase();
    if (!["json", "csv"].includes(ext)) {
      setFile(null);
      setParseError("Unsupported file type. Choose a .json or .csv file.");
      return;
    }
    setFile(selected);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseImportFile(selected.name, String(reader.result));
        if (parsed.length > MAX_IMPORT_TEMPLATES) {
          setTemplates([]);
          setParseError(
            `This file contains ${parsed.length} templates. The maximum is ${MAX_IMPORT_TEMPLATES} per import.`,
          );
          return;
        }
        setTemplates(parsed);
      } catch (err) {
        setTemplates([]);
        setParseError(err?.message || "Could not read this file.");
      }
    };
    reader.onerror = () => setParseError("Could not read this file.");
    reader.readAsText(selected);
  };

  const handleFileChange = (e) => ingestFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    ingestFile(e.dataTransfer.files?.[0]);
  };

  const handleModeChange = (e) => {
    setMode(e.target.value);
    resetPreview();
  };

  const runImport = async (dryRun) => {
    if (!templates.length || busy) return;
    setBusy(true);
    try {
      const res = await importReportTemplates({ templates, mode, dryRun });
      const summary = res?.data?.data;
      if (!summary)
        throw new Error(res?.data?.message || "Unexpected response.");
      if (dryRun) {
        setPreview(summary);
      } else {
        setFinalResult(summary);
        setPreview(null);
        const done = summary.created + summary.updated;
        toast.success(
          `Import complete — ${done} written, ${summary.skipped} skipped, ${summary.failed} failed.`,
        );
        if (done > 0 || summary.skipped > 0) onImported?.();
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Import failed.",
      );
    } finally {
      setBusy(false);
    }
  };

  const actionable = preview ? preview.created + preview.updated : 0;

  return (
    <Modal open={open} onClose={() => onClose()}>
      <Box sx={modalStyle}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            mb: 1,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
              Import Report Templates
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload JSON or CSV. Download the CSV template to see the required
              format — use multiple rows to add multiple parameters to the same
              report template. You will see a preview before anything is saved.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={downloadCsvTemplate}
            sx={{
              flexShrink: 0,
              borderColor: "#194AF5",
              color: "#194AF5",
              whiteSpace: "nowrap",
            }}
          >
            Download CSV Template
          </Button>
        </Box>

        {/* Dropzone / file picker */}
        <Box
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          sx={{
            border: "2px dashed",
            borderColor: dragOver ? "#194AF5" : "#C7D2FE",
            bgcolor: dragOver ? "#EEF2FF" : "#FAFBFF",
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            cursor: "pointer",
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".json,.csv"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          <FileUploadOutlinedIcon sx={{ fontSize: 34, color: "#194AF5" }} />
          <Typography sx={{ mt: 1, fontWeight: 600 }}>
            {file ? file.name : "Click to choose or drag a .json / .csv file"}
          </Typography>
          {templates.length > 0 && (
            <Chip
              size="small"
              label={`${templates.length} template(s) detected`}
              sx={{ mt: 1, bgcolor: "#ECFDF5", color: "#047857" }}
            />
          )}
        </Box>

        {parseError && (
          <Typography variant="body2" sx={{ mt: 1.5, color: "#DC2626" }}>
            {parseError}
          </Typography>
        )}

        {/* Required-field guidance (matches backend validation) */}
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            bgcolor: "#F8F9FF",
            borderRadius: 2,
            border: "1px solid #E0E4FF",
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Required fields
          </Typography>
          <Typography variant="body2" sx={{ color: "#4B5563" }}>
            <b>
              templateName <span style={{ color: "#DC2626" }}>*</span>
            </b>{" "}
            for every template, and{" "}
            <b>
              parameterName <span style={{ color: "#DC2626" }}>*</span>
            </b>{" "}
            for each parameter row. All other columns are optional.
          </Typography>
          <Typography variant="caption" sx={{ color: "#DC2626" }}>
            * Required fields
          </Typography>
        </Box>

        {/* Mode */}
        <FormControl sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            When a matching template already exists
          </Typography>
          <RadioGroup value={mode} onChange={handleModeChange}>
            <FormControlLabel
              value="skip"
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">
                  <b>Skip Existing</b> (recommended) — matching templates are
                  left unchanged.
                </Typography>
              }
            />
            <FormControlLabel
              value="upsert"
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">
                  <b>Update Existing</b> — existing same-scope templates may be
                  updated.
                </Typography>
              }
            />
          </RadioGroup>
        </FormControl>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
          <Button
            variant="outlined"
            onClick={() => runImport(true)}
            disabled={!templates.length || busy}
            sx={{ borderColor: "#194AF5", color: "#194AF5" }}
          >
            {busy && !finalResult ?
              <CircularProgress size={18} />
            : "Preview Import"}
          </Button>
          <Button
            variant="contained"
            onClick={() => runImport(false)}
            disabled={!preview || actionable === 0 || busy}
            sx={{ boxShadow: "none" }}
          >
            Confirm Import
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={resetAll} disabled={busy} sx={{ color: "#6B7280" }}>
            Reset
          </Button>
        </Box>

        {preview && actionable === 0 && (
          <Typography variant="body2" sx={{ mt: 1.5, color: "#B45309" }}>
            Nothing to import — no templates would be created or updated with
            the current file and mode.
          </Typography>
        )}
        {preview && preview.failed > 0 && actionable > 0 && (
          <Typography variant="body2" sx={{ mt: 1.5, color: "#B45309" }}>
            {preview.failed} row(s) failed validation and will not be imported.
          </Typography>
        )}

        {preview && <SummaryBlock title="Preview" summary={preview} />}
        {finalResult && (
          <SummaryBlock title="Import Result" summary={finalResult} />
        )}

        {/* Footer */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => onClose()}
            disabled={busy}
            sx={{ borderColor: "#194AF5", color: "#194AF5" }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ReportTemplateImportModal;
